import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {loadScene} from "../../store/routing/actions";
import styles from "./main-menu.module.css";
import {IApplicationState} from "../../store";
import {toggleMusic, toggleSounds} from "../../store/player/actions";
import {cssHelper} from "../../shared/utils/cssHelper";
import {AnimationSettingsProp, IInvite} from "../../store/settings/types";
import playBackground from "../../shared/sounds/playBackground";
import {AppScene} from "../../store/routing/AppScene";
import {Dispatch} from "redux";
import toDataURL from "../../shared/utils/toDataURL";
import {spread} from "../../shared/utils/spread";
import {equiprobable, randomInt} from "../../shared/utils/random";
import {TimelineLite, TweenMax, Animation} from "gsap";
import {IPlayerSettings} from "../../store/player/types";
import {LogLevel} from "../../store/log/types";
import {trackError} from "../../store/log/actions";
import {gameReset, trackAction} from "../../store/common/actions";
import {showDialog} from "../../store/dialog-manager/actions";
import {iif} from "../../shared/components/iif";
import playSound from "../../shared/sounds/playSound";
import {Overlay} from "../../shared/components/overlay/Overlay";
import {LoadingIndicator} from "../../shared/components/loading/LoadingIndicator";
import i18n from "../../shared/i18n";
import {ITracking} from "../../store/common/types";
import { SDK } from "../../sdk";
import AppStorePanel from "../../shared/components/app-store-panel/AppStorePanel";

export interface MainMenuSceneProps extends DispatchProp, AnimationSettingsProp {
    isLevelInProgress: boolean;
    settings: IPlayerSettings;
    isNewPlayer: boolean;
    invites: IInvite[];
    allowDebug: boolean;
    paymentsAreSupported: boolean;
    shareTracking?: ITracking;
    loadScene: (scene: AppScene) => void;
    lazyReload: () => void;
    toggleMusic: () => void;
    toggleSounds: () => void;
    showCredits: () => void;
}

class MainMenuScene extends PureComponent<MainMenuSceneProps, {loading: boolean}> {
    private readonly hostElement: React.RefObject<HTMLElement>;
    private readonly container: React.RefObject<HTMLDivElement>;
    private readonly activeTweens: Animation[] = [];

    constructor(props: MainMenuSceneProps, context: any) {
        super(props, context);

        this.hostElement = React.createRef();
        this.container = React.createRef();

        this.state = {
            loading: false
        };
    }

    render() {
        const { 
            leaderboards: supportsLeaderboards, 
            invites: supportsInvites,
            payments: supportsPayments} = SDK.instance.supports;
        const {
            isNewPlayer,
            settings,
            loadScene,
            toggleMusic,
            toggleSounds,
            showCredits,
            invites,
            paymentsAreSupported} = this.props;

        return (
            <section className={`${styles.container} main-menu`} ref={this.hostElement}>
                <i className={cssHelper(styles.icon, styles.icon_credits).toString()} onClick={(event) => this.onMenuItemClick(event, () => showCredits())}/>
                <i className={cssHelper(styles.icon, styles.icon_music).add(styles.icon_music_disabled, !settings.music).toString()} onClick={(event) => this.onMenuItemClick(event, () => toggleMusic())}/>
                <i className={cssHelper(styles.icon, styles.icon_volume).add(styles.icon_volume_disabled, !settings.sounds).toString()} onClick={(event) => this.onMenuItemClick(event, () => toggleSounds())}/>

                <figure className={`${styles.logo} logo`} onClick={(event) => this.onLogoClick(event)}/>
                <div className={styles.caption}>{i18n("mainmenu.header")}</div>

                <nav className={styles.menu_items}>
                    <button className={`${styles.menu_item} ${styles.play_button}`} onClick={(event) => this.onMenuItemClick(event, () => this.play())}>
                        <i className={`${styles.icon} ${styles.icon_play}`}/> {i18n("mainmenu.playbutton")}
                    </button>
                    {iif(invites.length > 0 && supportsLeaderboards, (
                        <button className={`${styles.menu_item} ${styles.competitions}`}
                            onClick={(event) => this.onMenuItemClick(event, () => loadScene('Leaderboards'))}>
                            <i className={`${styles.icon} ${styles.icon_hiscores}`}/> {i18n("mainmenu.leaderboards")}
                        </button>
                    ))}
                    <button className={`${styles.menu_item} ${styles.profile_button}`} onClick={(event) => this.onMenuItemClick(event, () => loadScene('Profile'))}>
                        <i className={`${styles.icon} ${styles.icon_profile}`}/> {i18n("mainmenu.profilebutton")}
                    </button>
                    <button disabled={isNewPlayer} className={styles.menu_item} onClick={(event) => this.onMenuItemClick(event, () => loadScene('History'))}>
                        <i className={`${styles.icon} ${styles.icon_history}`}/> {i18n("mainmenu.historybutton")}
                    </button>
                    {iif(invites.length > 0 && supportsInvites, (
                        <button className={styles.menu_item} onClick={(event) => this.onMenuItemClick(event, () => this.share(equiprobable(invites)))}>
                            <i className={`${styles.icon} ${styles.icon_share}`}/> {i18n("mainmenu.sharebutton")}
                        </button>
                    ))}
                    {iif(!window.DISABLE_PRODUCT_STORE && supportsPayments, (
                        <button disabled={!paymentsAreSupported} className={cssHelper(styles.menu_item, styles.store).toString()} onClick={(event) => this.onMenuItemClick(event, () => loadScene('Store'))}>
                            <i className={`${styles.icon} ${styles.icon_store}`}/> {i18n("inapppurchasesshop.defaultheader")}
                        </button>
                    ))}
                </nav>

                <div className={styles.app_store_panel_wrapper}>
                    <AppStorePanel/>
                </div>

                {iif(this.state.loading, (
                    <Overlay opacity={0.3}>
                        <LoadingIndicator/>
                    </Overlay>
                ))}
            </section>
        );
    }

    componentDidMount(): void {
        playBackground("main-menu");

        const {animation} = this.props;

        if (!this.hostElement.current || !animation.enabled)
            return;

        const logo = this.hostElement.current.getElementsByClassName(styles.logo);
        const caption = this.hostElement.current.getElementsByClassName(styles.caption);
        const menuItems = this.hostElement.current.querySelectorAll(`.${styles.menu_item}, .${styles.app_store_panel_wrapper}`);

        const timeline = new TimelineLite()
            .set(menuItems, {
                opacity: 0
            })
            .from(logo, 0.33, {
                scale: 0.66,
                opacity: 0,
            })
            .from(caption, 0.33, {
                y: 50,
                scale: 0.8,
                opacity: 0,
                delay: -0.125
            })
            .call(() => {
                new TimelineLite()
                    .staggerFromTo(menuItems, 0.33, {
                        y: 50,
                        opacity: 0,
                        delay: -0.025
                    }, {
                        y: 0,
                        opacity: 1,
                        cycle: {
                            opacity: (i: number) => (menuItems[i] as any).disabled ? 0.5 : 1
                        }
                    }, 0.125)
                    .timeScale(animation.timeScale);
            })
            .timeScale(animation.timeScale);

        this.activeTweens.push(timeline);
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    onLogoClick(event: React.MouseEvent) {
        const {animation} = this.props;

        playSound("mouth-pop");

        if (event && event.target && animation.enabled) {
            const tween = TweenMax.fromTo(event.target, 0.05, {
                scaleY: 1,
                skewX: 1,
                skewY: 1,
                opacity: 1,
            }, {
                scaleY: spread(0.9, 0.1),
                skewX: randomInt(-50, 50),
                skewY: randomInt(-50, 50),
                opacity: 0.5,
                repeat: 1,
                yoyo: true
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);
        }

        if (this.props.allowDebug) {
            this.props.lazyReload();
        }
    }

    onMenuItemClick(event: React.MouseEvent, action: () => void) {
        const {animation} = this.props;

        playSound("button-click");

        if (event && event.target && animation.enabled) {
            const tween = TweenMax.fromTo(event.target, 0.05, {
                scale: 1,
            },{
                scale: 1.05,
                repeat: 1,
                yoyo: true,
                onComplete: () => action()
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);
        } else {
            action();
        }
    }

    async share(invite: IInvite) {
        try {
            const shareImage = await toDataURL(invite.imageUrl);
            let data = invite.data;

            this.setState({
                loading: true
            });

            if (typeof data === "string") {
                try {
                    data = JSON.parse(data);
                } catch (error) {
                    this.props.dispatch(trackError({
                        logLevel: LogLevel.Warn,
                        category: "MainMenuScene",
                        message: "Could not deserialize invite data",
                        error: error,
                        data: invite
                    }));
                }
            }

            SDK.instance.invite({
                image: shareImage,
                text: i18n("mainmenu.share.text"),
                data,
                tracking: this.props.shareTracking
            });

            this.props.dispatch(trackAction({
                category: "game",
                object: "share",
                name: "click",
            }));
        } 
        catch (error) {
            this.props.dispatch(trackError({
                category: "MainMenuScene",
                message: "Failed to share user invite",
                error: error,
                data: invite,
            }));
        }

        this.setState({
            loading: false
        });
    }

    private play() {
        this.props.loadScene(this.props.isLevelInProgress ? 'LevelMap' : 'LevelSelection');
    }
}

export default connect(({ questions, settings, player, game, payments }: IApplicationState) => ({
        isLevelInProgress: !!game.levelInProgress
            && !game.levelMapProgress[game.levelInProgress].isCompleted
            && !!game.levelMaps[game.levelInProgress],
        settings: player.settings,
        animation: settings.animation,
        isNewPlayer: !Object.keys(questions.progress).length,
        invites: settings.social.invites,
        paymentsAreSupported: payments.isSupported && !!settings.store.products.length,
        allowDebug: settings.allowDebug,
        shareTracking: settings.social.inviteFriendTracking
}), (dispatch: Dispatch) => {
    let firstTimeClickTimestamp = 0;
    let clickCounter = 0;

    return {
        dispatch: dispatch,
        toggleMusic: () => dispatch(toggleMusic()),
        toggleSounds: () => dispatch(toggleSounds()),
        lazyReload() {
            if (new Date().getTime() > firstTimeClickTimestamp + 2000 || clickCounter > 5) {
                clickCounter = 0;
                firstTimeClickTimestamp = new Date().getTime();

                return;
            }

            if (clickCounter >= 5) {
                dispatch(gameReset());
            }

            clickCounter++;
        },

        loadScene: (scene: AppScene) => {
            dispatch(loadScene.request(scene, "main_menu"));
        },

        showCredits: () => dispatch(showDialog({
            type: "CreditsDialog",
            options: {}
        }))
    };
})(MainMenuScene);